import type { CompanyModules, ProjectModules } from './app-modules.consts';

type AppHelpModule = CompanyModules | ProjectModules;

export interface ShortcutInfo {
  keys: string[];
  translationKey: string;
  separators?: string[];
}

export type InfoPopupType = 'Dismissed' | 'InfoPopupDismissed';

export interface InfoPopupConfig {
  storageKey: string;
  view: string;
  titleKey: string;
  descriptionKey: string;
  stepKeys?: string[];
  type?: InfoPopupType;
  version?: string;
}

export interface HelpConfig<T extends string = string> {
  key: T;
  titleKey: string;
  shortcuts: ShortcutInfo[];
  infoPopup?: InfoPopupConfig;
}

export type HelpDefinition = { [Key in AppHelpModule]: HelpConfig<Key> } & Record<string, HelpConfig>;

const editableTableNavigationShortcuts: ShortcutInfo[] = [
  { keys: ['Ctrl', '↑', '↓', '←', '→'], translationKey: 'help.shortcuts.ctrlArrows', separators: ['+'] },
  { keys: ['Tab', 'Tab', 'Shift'], translationKey: 'help.shortcuts.tabNavigation', separators: ['/', '+'] },
];

const cellSelectionShortcuts: ShortcutInfo[] = [
  { keys: ['Ctrl', 'Mouse'], translationKey: 'help.shortcuts.ctrlMouseSelection' },
];

export const helpDefinitions: HelpDefinition = {
  project_setup: {
    key: 'project_setup',
    titleKey: 'help.setup',
    shortcuts: [],
    infoPopup: {
      storageKey: 'project-setup',
      view: 'ProjectSetupComponent',
      type: 'InfoPopupDismissed',
      titleKey: 'help.infoPopups.projectSetup.title',
      descriptionKey: 'help.infoPopups.projectSetup.description',
    },
  },
  project_details: {
    key: 'project_details',
    titleKey: 'help.projectDetails',
    shortcuts: [],
  },
  project_dashboard: {
    key: 'project_dashboard',
    titleKey: 'help.dashboard',
    shortcuts: [],
  },
  project_priceFactorsAndLabourCosts: {
    key: 'project_priceFactorsAndLabourCosts',
    titleKey: 'help.priceFactorsAndLabourCosts',
    shortcuts: [...editableTableNavigationShortcuts],
  },
  project_costEstimate: {
    key: 'project_costEstimate',
    titleKey: 'help.costEstimate',
    shortcuts: [...editableTableNavigationShortcuts],
    infoPopup: {
      storageKey: 'project-cost-estimate',
      view: 'CostEstimateComponent',
      type: 'InfoPopupDismissed',
      titleKey: 'help.infoPopups.projectCostEstimate.title',
      descriptionKey: 'help.infoPopups.projectCostEstimate.description',
    },
  },
  project_timeTracker: {
    key: 'project_timeTracker',
    titleKey: 'help.timeTracker',
    shortcuts: [...editableTableNavigationShortcuts, ...cellSelectionShortcuts],
    infoPopup: {
      storageKey: 'project-time-tracker',
      view: 'TimeTrackerOverviewComponent',
      type: 'InfoPopupDismissed',
      titleKey: 'help.infoPopups.projectTimeTracker.title',
      descriptionKey: 'help.infoPopups.projectTimeTracker.description',
    },
  },
  project_progress: {
    key: 'project_progress',
    titleKey: 'help.progress',
    shortcuts: [],
    infoPopup: {
      storageKey: 'project-progress',
      view: 'ProjectProgressComponent',
      type: 'InfoPopupDismissed',
      titleKey: 'help.infoPopups.projectProgress.title',
      descriptionKey: 'help.infoPopups.projectProgress.description',
    },
  },
  project_employeeAssignment: {
    key: 'project_employeeAssignment',
    titleKey: 'help.employeeAssignment',
    shortcuts: [...cellSelectionShortcuts],
    infoPopup: {
      storageKey: 'project-employee-assignment',
      view: 'EmployeeAssignmentComponent',
      type: 'InfoPopupDismissed',
      titleKey: 'help.infoPopups.employeeAssignment.title',
      descriptionKey: 'help.infoPopups.employeeAssignment.description',
    },
  },
  project_projectPlanning: {
    key: 'project_projectPlanning',
    titleKey: 'help.projectPlanning',
    shortcuts: [],
  },
  project_employeeHours: {
    key: 'project_employeeHours',
    titleKey: 'help.employeeHours',
    shortcuts: [...editableTableNavigationShortcuts],
  },
  project_qaCheckMatrix: {
    key: 'project_qaCheckMatrix',
    titleKey: 'help.qaCheckMatrix',
    shortcuts: [],
  },
  project_workingHours: {
    key: 'project_workingHours',
    titleKey: 'help.workingHours',
    shortcuts: [],
  },
  project_dataAudit: {
    key: 'project_dataAudit',
    titleKey: 'help.dataAudit',
    shortcuts: [],
  },
  project_preferences: {
    key: 'project_preferences',
    titleKey: 'help.preferences',
    shortcuts: [],
  },
  project_files: {
    key: 'project_files',
    titleKey: 'help.files',
    shortcuts: [],
    infoPopup: {
      storageKey: 'project-files',
      view: 'ProjectFilesComponent',
      type: 'InfoPopupDismissed',
      titleKey: 'help.infoPopups.projectFiles.title',
      descriptionKey: 'help.infoPopups.projectFiles.description',
    },
  },
  project_plans: {
    key: 'project_plans',
    titleKey: 'help.plans',
    shortcuts: [],
    infoPopup: {
      storageKey: 'project-plans',
      view: 'ProjectFilesComponent',
      type: 'InfoPopupDismissed',
      titleKey: 'help.infoPopups.projectPlans.title',
      descriptionKey: 'help.infoPopups.projectPlans.description',
    },
  },
  project_master_project: {
    key: 'project_master_project',
    titleKey: 'help.masterProject',
    shortcuts: [],
  },
  project_sub_projects: {
    key: 'project_sub_projects',
    titleKey: 'help.subProjects',
    shortcuts: [],
  },
  project_workTasks: {
    key: 'project_workTasks',
    titleKey: 'help.workTasks',
    shortcuts: [],
  },
  project_materialAssignment: {
    key: 'project_materialAssignment',
    titleKey: 'help.materialAssignment',
    shortcuts: [...editableTableNavigationShortcuts],
    infoPopup: {
      storageKey: 'project-material-assignment',
      view: 'MaterialAssignmentComponent',
      type: 'InfoPopupDismissed',
      titleKey: 'help.infoPopups.projectMaterialAssignment.title',
      descriptionKey: 'help.infoPopups.projectMaterialAssignment.description',
    },
  },
  project_constructionAreas: {
    key: 'project_constructionAreas',
    titleKey: 'help.constructionAreas',
    shortcuts: [
      { keys: ['Insert'], translationKey: 'help.shortcuts.insert' },
      { keys: ['↑', '↓'], translationKey: 'help.shortcuts.arrowUpDown' },
      ...editableTableNavigationShortcuts,
    ],
  },
  project_activities: {
    key: 'project_activities',
    titleKey: 'help.activities',
    shortcuts: [
      { keys: ['Insert'], translationKey: 'help.shortcuts.insert' },
      { keys: ['↑', '↓'], translationKey: 'help.shortcuts.arrowUpDown' },
      ...editableTableNavigationShortcuts,
    ],
  },
  project_phases: {
    key: 'project_phases',
    titleKey: 'help.phases',
    shortcuts: [
      { keys: ['Insert'], translationKey: 'help.shortcuts.insert' },
      { keys: ['↑', '↓'], translationKey: 'help.shortcuts.arrowUpDown' },
      ...editableTableNavigationShortcuts,
    ],
  },
  company_details: {
    key: 'company_details',
    titleKey: 'help.companyDetails',
    shortcuts: [],
  },
  company_dashboard: {
    key: 'company_dashboard',
    titleKey: 'help.dashboard',
    shortcuts: [],
  },
  company_projects: {
    key: 'company_projects',
    titleKey: 'help.projects',
    shortcuts: [],
  },
  company_employeeHours: {
    key: 'company_employeeHours',
    titleKey: 'help.employeeHours',
    shortcuts: [],
  },
  company_workingHours: {
    key: 'company_workingHours',
    titleKey: 'help.workingHours',
    shortcuts: [],
  },
  company_customers: {
    key: 'company_customers',
    titleKey: 'help.customers',
    shortcuts: [],
    infoPopup: {
      storageKey: 'customers',
      view: 'MasterDataCustomersComponent',
      type: 'InfoPopupDismissed',
      titleKey: 'help.infoPopups.customers.title',
      descriptionKey: 'help.infoPopups.customers.description',
    },
  },
  company_materials: {
    key: 'company_materials',
    titleKey: 'help.materials',
    shortcuts: [],
  },
  company_materialPackages: {
    key: 'company_materialPackages',
    titleKey: 'help.materialPackages',
    shortcuts: [],
  },
  company_priceFactors: {
    key: 'company_priceFactors',
    titleKey: 'help.priceFactors',
    shortcuts: [],
  },
  company_estimateParameters: {
    key: 'company_estimateParameters',
    titleKey: 'help.estimateParameters',
    shortcuts: [],
  },
  company_activityTemplates: {
    key: 'company_activityTemplates',
    titleKey: 'help.activityTemplates',
    shortcuts: [
      { keys: ['Insert'], translationKey: 'help.shortcuts.insert' },
      { keys: ['↑', '↓'], translationKey: 'help.shortcuts.arrowUpDown' },
      ...editableTableNavigationShortcuts,
    ],
  },
  company_phaseTemplates: {
    key: 'company_phaseTemplates',
    titleKey: 'help.phaseTemplates',
    shortcuts: [
      { keys: ['Insert'], translationKey: 'help.shortcuts.insert' },
      { keys: ['↑', '↓'], translationKey: 'help.shortcuts.arrowUpDown' },
      ...editableTableNavigationShortcuts,
    ],
  },
  company_qaCheckItems: {
    key: 'company_qaCheckItems',
    titleKey: 'help.qaCheckItems',
    shortcuts: [{ keys: ['Insert'], translationKey: 'help.shortcuts.insert' }, ...editableTableNavigationShortcuts],
    infoPopup: {
      storageKey: 'quality-check-item-templates',
      view: 'QualityCheckItemTemplatesComponent',
      type: 'InfoPopupDismissed',
      titleKey: 'help.infoPopups.qualityCheckItems.title',
      descriptionKey: 'help.infoPopups.qualityCheckItems.description',
      stepKeys: [
        'help.infoPopups.qualityCheckItems.step1',
        'help.infoPopups.qualityCheckItems.step2',
        'help.infoPopups.qualityCheckItems.step3',
        'help.infoPopups.qualityCheckItems.step4',
      ],
    },
  },
  company_subscription: {
    key: 'company_subscription',
    titleKey: 'help.subscription',
    shortcuts: [],
  },
  company_users: {
    key: 'company_users',
    titleKey: 'help.users',
    shortcuts: [],
  },
  company_dataAudit: {
    key: 'company_dataAudit',
    titleKey: 'help.dataAudit',
    shortcuts: [],
  },
  company_salesData: {
    key: 'company_salesData',
    titleKey: 'help.salesData',
    shortcuts: [],
  },
  company_timeAndCostCategories: {
    key: 'company_timeAndCostCategories',
    titleKey: 'help.timeAndCostCategories',
    shortcuts: [],
    infoPopup: {
      storageKey: 'time-entry-categories',
      view: 'TimeEntryCategoriesComponent',
      type: 'InfoPopupDismissed',
      titleKey: 'masterData.timeAndCostCategories.infoPopup.title',
      descriptionKey: 'masterData.timeAndCostCategories.infoPopup.description',
      stepKeys: [
        'masterData.timeAndCostCategories.infoPopup.step1',
        'masterData.timeAndCostCategories.infoPopup.step2',
        'masterData.timeAndCostCategories.infoPopup.step3',
        'masterData.timeAndCostCategories.infoPopup.step4',
      ],
    },
  },
  company_serviceTimeTracker: {
    key: 'company_serviceTimeTracker',
    titleKey: 'help.serviceTimeTracker',
    shortcuts: [...editableTableNavigationShortcuts, ...cellSelectionShortcuts],
    infoPopup: {
      storageKey: 'service-time-tracker',
      view: 'ServiceTimeTrackerComponent',
      type: 'InfoPopupDismissed',
      titleKey: 'help.infoPopups.serviceTimeTracker.title',
      descriptionKey: 'help.infoPopups.serviceTimeTracker.description',
    },
  },
  company_staffing: {
    key: 'company_staffing',
    titleKey: 'help.staffing',
    shortcuts: [...cellSelectionShortcuts],
    infoPopup: {
      storageKey: 'staffing',
      view: 'StaffingEmployeeAssignmentComponent',
      type: 'InfoPopupDismissed',
      titleKey: 'help.infoPopups.staffing.title',
      descriptionKey: 'help.infoPopups.staffing.description',
    },
  },
  report_employeeHours: {
    key: 'report_employeeHours',
    titleKey: 'help.employeeHoursReport',
    shortcuts: [],
  },
  company_employeeHoursPeriodLock: {
    key: 'company_employeeHoursPeriodLock',
    titleKey: 'help.employeeHoursPeriodLock',
    shortcuts: [],
  },
  company_templatesAndDefaults: {
    key: 'company_templatesAndDefaults',
    titleKey: 'help.templatesAndDefaults',
    shortcuts: [...editableTableNavigationShortcuts],
  },
  company_workTasks: {
    key: 'company_workTasks',
    titleKey: 'help.workTasks',
    shortcuts: [],
  },
  'templates-and-defaults': {
    key: 'templates-and-defaults',
    titleKey: 'help.templatesAndDefaults',
    shortcuts: [...editableTableNavigationShortcuts],
    infoPopup: {
      storageKey: 'templates-and-defaults',
      view: 'TemplatesAndDefaultsComponent',
      type: 'InfoPopupDismissed',
      titleKey: 'help.infoPopups.templatesAndDefaults.title',
      descriptionKey: 'help.infoPopups.templatesAndDefaults.description',
    },
  },
};
